import upperFirst from 'lodash/upperFirst'
import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'temporal-polyfill/global'

import { Provider } from 'src/api/gen/api'
import { getProviderEntries } from 'src/commons/filters/utils'
import useSafeEffect from 'src/commons/hooks/useSafeEffect'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { ORDERED_GRANULARITIES } from 'src/commons/orderedList'
import { GranularityEnum, IFilters } from 'src/commons/types'
import { useMapLayer } from 'src/layout/MapLayerWrapper'
import CheckboxListFilter from 'src/muiv5/components/commons/SidePanel/Filters/CheckboxListFilter/CheckboxListFilter'
import TimeRangeFilterPro from 'src/muiv5/components/commons/SidePanel/Filters/TimeRangeFilter/TimeRangeFilterPro'
import { SidePanel } from 'src/muiv5/components/commons/SidePanel/SidePanel'

const timeTranslation = enumsTranslationKeys.Time as {
  [index: string]: string
}

interface IProps {
  defaultFilters: IFilters
  initialFilters: IFilters
  providers: Provider[]
}

export const PolicyFilterPanel = forwardRef(function (props: IProps, ref) {
  const { t } = useTranslation()
  const mapLayers = useMapLayer()

  const [filters, setFilters] = useState(props.initialFilters)
  useImperativeHandle(ref, () => filters, [filters])

  function onFilterChange(key: string, newValue: any) {
    return setFilters({ ...filters, [key]: newValue })
  }

  const permitLayers = useMemo(() => mapLayers.filter((layer) => layer.permitLayer), [mapLayers])
  // There can only be one permit layer per jurisdiction
  const jurisdictionPermitLayerId = useMemo(() => {
    if (permitLayers.length > 0) {
      return permitLayers[0].id
    }

    return undefined
  }, [permitLayers])

  useSafeEffect(() => {
    if (jurisdictionPermitLayerId) {
      onFilterChange('layer', jurisdictionPermitLayerId)
    }
  }, [jurisdictionPermitLayerId])

  function onFromChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('range', { to: filters.range.to, from: value })
  }

  function onToChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('range', { to: value, from: filters.range.from })
  }

  function onBothChanged(valueFrom?: Temporal.ZonedDateTime, valueTo?: Temporal.ZonedDateTime) {
    onFilterChange('range', { from: valueFrom, to: valueTo })
  }

  const providerEntries = getProviderEntries(props.providers)

  return (
    <SidePanel
      headerProps={{
        title: t('filtersPanel.title.default'),
        onReset: () => setFilters(props.defaultFilters),
      }}
    >
      <TimeRangeFilterPro
        filterId={'range'}
        format="L"
        values={{
          from: filters.range.from,
          to: filters.range.to,
        }}
        title={upperFirst(t('time range'))}
        onFromChanged={onFromChanged}
        onToChanged={onToChanged}
        onBothChanged={onBothChanged}
        disableFuture
        defaultExpanded
      />
      <CheckboxListFilter
        filterId={'granularity'}
        items={ORDERED_GRANULARITIES.map((key) => ({
          id: GranularityEnum[key as keyof typeof GranularityEnum].toString(),
          label: upperFirst(t(timeTranslation[key])),
        }))}
        values={[GranularityEnum[filters['granularity'] as keyof typeof GranularityEnum].toString()]}
        title={upperFirst(t('granularity'))}
        unique
        onFilterChange={(key: string, newValue: string[]) =>
          onFilterChange(key, GranularityEnum[newValue[newValue.length - 1] as keyof typeof GranularityEnum])
        }
        displayCheckbox
      />
      <CheckboxListFilter
        filterId={'providers'}
        items={providerEntries}
        values={filters['providers']}
        title={upperFirst(t('filtersPanel.provider'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
    </SidePanel>
  )
})

export default PolicyFilterPanel
